import {testIdProps} from '@github-ui/test-id-props'
import {Button} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {AlertFillIcon, CircleIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {memo, type RefObject, useCallback, useState} from 'react'

import {MergeabilitySidesheet} from './MergeabilitySidesheet'
import {presentationForStatus, Status} from '../helpers/mergeability-status'
import {getStatus, getTooltipDescription} from '../helpers/merge-status-button'
import {useMergeBoxPageData, useMergeBoxPageDataQueryKey} from '../page-data/loaders/use-merge-box-page-data'
import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import {DockedPanel, useIsDockedPanelOpen} from '@github-ui/pull-requests/contexts/DockedPanelContext'
import type {MergeBoxPageData} from '../page-data/payloads/merge-box'
import {useMergeabilityLiveUpdates, type MergeabilityLiveUpdateChannels} from '../hooks/use-mergeability-live-updates'
import {useStatusChecksPageDataWithoutError} from '../page-data/loaders/use-status-checks-page-data'
import type {StatusChecksPageData} from '../page-data/payloads/status-checks'
import styles from './MergeStatusButton.module.css'
import {useAnalytics} from '@github-ui/use-analytics'

interface MergeStatusButtonCommonProps {
  mergeStatusButtonRef: RefObject<HTMLButtonElement | null>
  toggleMergeabilitySidesheet: (isOpen: boolean) => void
}

/**
 * A button that displays the merge status of a pull request.
 * Opens the mergeability sidesheet.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the component name
export const MergeStatusButtonWrapper = memo(function MergeStatusButtonWrapper({
  channels,
  headSha,
  helpUrl,
  viewerLogin,
  isDockedMode,
  onToggleDockedPanel,
  mergeStatusButtonRef,
  size,
  variant,
  buttonClasses,
}: {
  channels: MergeabilityLiveUpdateChannels
  headSha: string
  helpUrl: string
  viewerLogin: string
  /** When true, suppresses the MergeabilitySidesheet and delegates click to onToggleDockedPanel */
  isDockedMode: boolean
  /** Called on button click when isDockedMode is true */
  onToggleDockedPanel: () => void
  mergeStatusButtonRef: RefObject<HTMLButtonElement | null>
  size?: 'small' | 'medium'
  variant?: 'default' | 'invisible'
  buttonClasses?: string
}) {
  const {mergeMethod} = useMergeMethodContext()
  const {data} = useMergeBoxPageData({mergeMethod, bypassRequirements: false})
  const queryKey = useMergeBoxPageDataQueryKey()
  const {data: statusChecksData} = useStatusChecksPageDataWithoutError({
    pullRequestHeadSha: headSha,
  })

  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)
  const [mergeabilitySidesheetIsOpen, setMergeabilitySidesheetIsOpen] = useState(false)
  const toggleMergeabilitySidesheet = useCallback(
    (isOpen: boolean) => {
      if (isOpen && isDockedMode) {
        onToggleDockedPanel()
        return
      }
      setMergeabilitySidesheetIsOpen(isOpen)
    },
    [isDockedMode, onToggleDockedPanel],
  )

  useMergeabilityLiveUpdates({queryKey, channels})

  const isLoading = !data || !statusChecksData

  return (
    <>
      <MergeStatusButton
        className={buttonClasses}
        isLoading={isLoading}
        pullRequest={data?.pullRequest}
        mergeRequirements={data?.mergeRequirements}
        statusChecksData={statusChecksData}
        mergeStatusButtonRef={mergeStatusButtonRef}
        toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
        isPanelOpen={isDockedMode ? isDockedPanelOpen : mergeabilitySidesheetIsOpen}
        size={size}
        variant={variant}
      />
      {!isDockedMode && (
        <MergeabilitySidesheet
          helpUrl={helpUrl}
          mergeStatusButtonRef={mergeStatusButtonRef}
          mergeabilitySidesheetIsOpen={mergeabilitySidesheetIsOpen}
          toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
          viewerLogin={viewerLogin}
          channels={channels}
        />
      )}
    </>
  )
})

export type MergeStatusButtonData = MergeBoxPageData

interface MergeStatusButtonProps extends MergeStatusButtonCommonProps {
  isLoading?: boolean
  pullRequest?: MergeBoxPageData['pullRequest']
  mergeRequirements?: MergeBoxPageData['mergeRequirements']
  statusChecksData?: StatusChecksPageData
  isPanelOpen: boolean
  size?: 'small' | 'medium'
  variant?: 'default' | 'invisible'
  className?: string
}

export function MergeStatusButton({
  isLoading,
  pullRequest,
  mergeRequirements,
  statusChecksData,
  mergeStatusButtonRef,
  toggleMergeabilitySidesheet,
  isPanelOpen,
  size = 'medium',
  className,
  variant = 'default',
}: MergeStatusButtonProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  if (!mergeStatusButtonRef) return null

  const status = getStatus(isLoading, pullRequest, mergeRequirements, statusChecksData)
  const presentation =
    !status || status === Status.NonactionableFailure || status === Status.Unknown
      ? undefined
      : presentationForStatus(status)
  const tooltipDescription = getTooltipDescription(status, statusChecksData, mergeRequirements)

  // we dont want to duplicate icons on the Overview page, so manually overriding these to be circles representing merge status, not PR status
  const hasPullRequestStatusIcon = status === Status.Draft || status === Status.Merged || status === Status.Closed

  // override the merge conflicts with Alert
  const statusIcon = status === Status.MergeConflicts ? AlertFillIcon : (presentation?.icon ?? CircleIcon)

  // Keep the tooltip mounted so status refreshes do not replace the focused button.
  return (
    <Tooltip
      text={tooltipDescription ?? ''}
      type="description"
      _privateDisableTooltip={!tooltipDescription}
      popover={tooltipDescription ? 'auto' : undefined}
    >
      <Button
        ref={mergeStatusButtonRef}
        leadingVisual={hasPullRequestStatusIcon ? CircleIcon : statusIcon}
        variant={variant}
        aria-expanded={isPanelOpen}
        {...testIdProps('merge-status-button')}
        className={clsx(presentation?.iconColorClass && styles[presentation.iconColorClass], className)}
        onClick={() => {
          if (toggleMergeabilitySidesheet) {
            toggleMergeabilitySidesheet(true)
            sendAnalyticsEvent('merge_status_side_panel.open', 'MERGE_STATUS_SIDE_PANEL_BUTTON')
          }
        }}
        size={size}
        loading={isLoading || status === Status.Unknown}
        loadingAnnouncement="Loading merge status"
        inactive={isLoading || status === Status.Unknown}
      >
        <span className={styles.labelFull}>Merge status</span>
        <span className={styles.labelShort}>Status</span>
      </Button>
    </Tooltip>
  )
}
